import { clamp, isString, isEqual, isNil } from 'lodash';
import { v4 as uuid } from 'uuid';

import * as actions from '@common-modules/mediaDetails/actions';
import {
  BoundingPolyRect,
  TextOverlay,
  UDRsPolyAsset,
  UDRsPolyAssetStatus,
  UDRsPolyAssetGroup,
  UDRsPolyAssetGroupSeriesItem,
  Point,
} from '@common-modules/mediaDetails/models';

import { getUDRGroupIntervalBasedSegments } from '../../selectors/clusters';

import { namespace, WorkerState } from '../../models';
import { defaultState } from '../../store';
import {
  checkSameBoundingPoly,
  findFrameIndex,
  frameAlignStartTimeMs,
  frameAlignStopTimeMs,
  convertFrameIndexToTimeMs,
  ElementOf,
} from '@utils';
import { arrayHasContents, arrayHasLength } from '@common/shared/util';
import { CaseReducer, createReducer, Draft } from '@reduxjs/toolkit';
import { GROUPED_POLY_TYPE } from '@helpers/constants';
import {
  isTextOnlyOverlayGroup,
  isUdrAssetPolyType,
  resolveTextOverlayRect,
} from '@helpers/textOverlay';
import { produce, produceWithPatches } from 'immer';
import { selectGlobalSettings } from '@common/web-worker/selectors';
import { addBackgroundBlurEdits } from '../view';

type Re<P = unknown> = CaseReducer<WorkerState, { payload: P; type: string }>;

const NEW_UDR_LENGTH_MS = 1000;
const NEW_SHORT_UDR_LENGTH_MS = 100;
// Text overlays default to a short, readable duration on creation.
const NEW_TEXT_OVERLAY_LENGTH_MS = 3_000;

const onNewOverlay: Re<{
  readonly id: string;
  readonly groupId: string;
  readonly currentPosition: number;
  readonly boundingPoly: BoundingPolyRect;
  readonly shift: boolean;
  readonly textOverlay?: TextOverlay;
  readonly startTimeMs?: number;
  readonly stopTimeMs?: number;
}> = (state, { payload }) => {
  const mediaDuration = state.mediaDuration;
  const selectedUDRGroupId = payload.groupId;

  const polyId = payload.id;

  if (
    state.udrAsset.boundingPolys[selectedUDRGroupId]?.status ===
    UDRsPolyAssetStatus.PENDING
  ) {
    return;
  }

  const fps = state.tdo?.details?.veritoneFile?.videoFrameRate;

  // An explicit start/stop (e.g. a text overlay auto-fitted to a silent audio
  // region) overrides the default fixed-length span from currentPosition.
  let startTimeMs = clamp(
    payload.startTimeMs ?? payload.currentPosition,
    0,
    mediaDuration * 1000 - 1
  );
  // subtract 1 ms from clamp max in case play to end of video and then add UDR

  // if fps, start at beginning of frame
  if (fps) {
    startTimeMs = frameAlignStartTimeMs(startTimeMs, fps);
  }
  const newLengthMs = payload.textOverlay
    ? NEW_TEXT_OVERLAY_LENGTH_MS
    : payload.shift
      ? NEW_UDR_LENGTH_MS
      : NEW_SHORT_UDR_LENGTH_MS;
  let stopTimeMs = clamp(
    payload.stopTimeMs ?? startTimeMs + newLengthMs,
    0,
    mediaDuration * 1000
  );

  // IMAGE-TODO quick way to add image support
  if (stopTimeMs === 0) {
    stopTimeMs = 1000;
  }

  // if fps, align with the end of final frame
  if (fps) {
    stopTimeMs = frameAlignStopTimeMs(stopTimeMs, fps);
    stopTimeMs = Math.ceil(stopTimeMs - 1); // back off so displays as 1ms less the start time for next frame
  }
  const filteredBoundingPoly: BoundingPolyRect = [
    clampPoint(payload.boundingPoly[0]),
    clampPoint(payload.boundingPoly[1]),
    clampPoint(payload.boundingPoly[2]),
    clampPoint(payload.boundingPoly[3]),
  ];

  const isTextOverlayCreate = !!payload.textOverlay;

  const basePoly = {
    id: polyId, // GTODO do we need this saved in two locations?
    startTimeMs,
    stopTimeMs,
    object: {
      id: polyId, // GTODO do we need this saved in two locations?
      overlayObjectType: isTextOverlayCreate
        ? ('text_overlay' as const)
        : ('udr' as const),
      boundingPoly: filteredBoundingPoly,
    },
  };

  let selectedPolyGroups = {
    ...state.selectedPolyGroups,
    [polyId]: true,
  };

  const [newState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      const udrGroup = draft.udrAsset.boundingPolys?.[selectedUDRGroupId];
      if (isString(selectedUDRGroupId) && udrGroup && !isTextOverlayCreate) {
        const series = udrGroup.series;

        const seriesStart = sliceUdrGroupSeriesEndAtTime(
          series,
          basePoly.startTimeMs
        );
        let seriesEnd = sliceUdrGroupSeriesStartAtTime(
          series,
          basePoly.stopTimeMs
        );

        const lastItemFromSeriesStart = seriesStart.at(-1);
        const firstItemFromSeriesEnd = seriesEnd[0];

        if (
          lastItemFromSeriesStart &&
          firstItemFromSeriesEnd &&
          lastItemFromSeriesStart.id === firstItemFromSeriesEnd.id
        ) {
          const [_first, ...rest] = seriesEnd;
          const id = uuid();

          seriesEnd = [
            {
              ...firstItemFromSeriesEnd,
              id,
              object: { ...firstItemFromSeriesEnd.object, id },
            },
            ...rest,
          ];
          selectedPolyGroups = { ...selectedPolyGroups, [id]: true };
        }

        const newSeries = seriesStart.concat([basePoly], seriesEnd);

        Object.assign(draft.udrAsset.boundingPolys, {
          [selectedUDRGroupId]: {
            ...udrGroup,
            series: newSeries,
          },
        });
      } else {
        Object.assign(draft.udrAsset.boundingPolys, {
          [selectedUDRGroupId]: {
            userLabel: isTextOverlayCreate
              ? nextTextOverlayLabel(draft.udrAsset.boundingPolys)
              : nextGroupLabel(draft.udrAsset.boundingPolys),
            groupId: selectedUDRGroupId,
            clusterId: undefined, // selectedUDRGroupId,
            status: UDRsPolyAssetStatus.SINGLE,
            series: [basePoly],
            ...(isTextOverlayCreate && payload.textOverlay
              ? {
                  // Born in the same shape the save adapter writes (VE-30942):
                  // no basePoly (no OT engine seed), no shapeType (always a
                  // rectangle, and drawTextOverlay ignores it), no
                  // redactionConfig (it was only ever the fillType type-tag).
                  // An unpinned overlay carries its position as customRect from
                  // the start; a pinned one derives its box from the pins.
                  textOverlay:
                    payload.textOverlay.screenPositionHorizontal ||
                    payload.textOverlay.screenPositionVertical
                      ? payload.textOverlay
                      : {
                          ...payload.textOverlay,
                          customRect: filteredBoundingPoly,
                        },
                }
              : { basePoly }),
          },
        });
      }

      draft.tdoIsChanged = true;
      draft.highlightedOverlay = {
        id: polyId,
        timeMs: payload.currentPosition,
        groupId: payload.groupId,
        type: isTextOverlayCreate ? 'text_overlay' : 'udr',
      };

      draft.selectedPolyGroups = selectedPolyGroups;

      // check if we should create background blur
      const globalSettings = selectGlobalSettings({
        [namespace]: draft,
      });

      let addBackgroundBlur = false;
      let backgroundBlurLevel = globalSettings.defaultBackgroundBlurLevel;
      if (
        !isTextOverlayCreate &&
        globalSettings.autoAddBackgroundBlur &&
        globalSettings.objectTypeEffects['udr'].redactionConfig.fillType ===
          'inverse_blur' &&
        !draft.udrAsset.boundingPolys[payload.groupId]?.redactionConfig
      ) {
        addBackgroundBlur = true;
      }
      if (
        draft.udrAsset.boundingPolys[payload.groupId]?.redactionConfig
          ?.addBackgroundBlur
      ) {
        addBackgroundBlur = true;
        backgroundBlurLevel =
          draft.udrAsset.boundingPolys[payload.groupId]?.redactionConfig
            ?.addBackgroundBlur;
      }

      if (addBackgroundBlur && backgroundBlurLevel) {
        addBackgroundBlurEdits(
          draft,
          [selectedUDRGroupId],
          [],
          backgroundBlurLevel
        );
      }
    }
  );

  return produce(newState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

export const onAddTextOverlaySegment: Re<{
  groupId: string;
  startTimeMs: number;
  stopTimeMs: number;
}> = (state, { payload }) => {
  const group = state.udrAsset.boundingPolys[payload.groupId];
  if (!group) {
    return;
  }

  const mediaDuration = state.mediaDuration;
  const fps = state.tdo?.details?.veritoneFile?.videoFrameRate;

  let startTimeMs = clamp(payload.startTimeMs, 0, mediaDuration * 1000 - 1);
  let stopTimeMs = clamp(payload.stopTimeMs, 0, mediaDuration * 1000);
  if (fps) {
    startTimeMs = frameAlignStartTimeMs(startTimeMs, fps);
    stopTimeMs = frameAlignStopTimeMs(stopTimeMs, fps);
    stopTimeMs = Math.ceil(stopTimeMs - 1); // back off so displays 1ms before next frame
  }
  if (startTimeMs >= stopTimeMs) {
    return;
  }

  // Prevent overlap: reject the segment if it intersects any existing one.
  const overlaps = group.series.some(
    (s) => startTimeMs < s.stopTimeMs && stopTimeMs > s.startTimeMs
  );
  if (overlaps) {
    return;
  }

  // Seed the new segment with the group's box. This used to hunt for the
  // nearest-in-time segment's geometry (VE-25637 #7) because segments could
  // each hold a different rect; a text overlay's box is group-level now, so
  // every segment resolves to the same value and there is nothing to choose
  // between (VE-30942).
  const currentBoundingPoly = resolveTextOverlayRect(group);
  if (!currentBoundingPoly) {
    return;
  }

  const polyId = uuid();
  const newItem = {
    id: polyId,
    startTimeMs,
    stopTimeMs,
    object: {
      id: polyId,
      overlayObjectType: 'text_overlay' as const,
      boundingPoly: currentBoundingPoly,
    },
  };

  const selectedPolyGroups = {
    ...state.selectedPolyGroups,
    [polyId]: true,
  };

  const [newState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      const draftGroup = draft.udrAsset.boundingPolys[payload.groupId];
      if (!draftGroup) {
        return;
      }
      const nextSeries = [...draftGroup.series, newItem].sort(
        (a, b) => a.startTimeMs - b.startTimeMs
      );
      draftGroup.series = nextSeries;
      draftGroup.status =
        nextSeries.length > 1
          ? UDRsPolyAssetStatus.GROUP
          : UDRsPolyAssetStatus.SINGLE;

      draft.tdoIsChanged = true;
      draft.highlightedOverlay = {
        id: polyId,
        timeMs: startTimeMs,
        groupId: payload.groupId,
        type: 'text_overlay',
      };
      draft.selectedPolyGroups = selectedPolyGroups;
    }
  );

  return produce(newState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

export const onMergeTextOverlaySegments: Re<{
  groupId: string;
  keepId: string;
  dropId: string;
}> = (state, { payload }) => {
  const group = state.udrAsset.boundingPolys[payload.groupId];
  if (!group) {
    return;
  }
  if (payload.keepId === payload.dropId) {
    return;
  }
  const keep = group.series.find((s) => s.id === payload.keepId);
  const drop = group.series.find((s) => s.id === payload.dropId);
  if (!keep || !drop) {
    return;
  }

  // The merged segment spans the union of the two. Only merge ADJACENT segments
  // (nothing between them), otherwise the widened segment would overlap the
  // in-between ones and break the no-overlap invariant.
  const newStart = Math.min(keep.startTimeMs, drop.startTimeMs);
  const newStop = Math.max(keep.stopTimeMs, drop.stopTimeMs);
  const hasSegmentBetween = group.series.some(
    (s) =>
      s.id !== payload.keepId &&
      s.id !== payload.dropId &&
      s.startTimeMs < newStop &&
      s.stopTimeMs > newStart
  );
  if (hasSegmentBetween) {
    return;
  }

  const selectedPolyGroups = { ...state.selectedPolyGroups };
  delete selectedPolyGroups[payload.dropId];
  selectedPolyGroups[payload.keepId] = true;

  const [newState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      const draftGroup = draft.udrAsset.boundingPolys[payload.groupId];
      if (!draftGroup) {
        return;
      }
      const nextSeries = draftGroup.series
        .filter((s) => s.id !== payload.dropId)
        .map((s) =>
          s.id === payload.keepId
            ? { ...s, startTimeMs: newStart, stopTimeMs: newStop }
            : s
        )
        .sort((a, b) => a.startTimeMs - b.startTimeMs);
      draftGroup.series = nextSeries;
      draftGroup.status =
        nextSeries.length > 1
          ? UDRsPolyAssetStatus.GROUP
          : UDRsPolyAssetStatus.SINGLE;

      draft.tdoIsChanged = true;
      draft.highlightedOverlay = {
        id: payload.keepId,
        timeMs: newStart,
        groupId: payload.groupId,
        type: 'text_overlay',
      };
      draft.selectedPolyGroups = selectedPolyGroups;
    }
  );

  return produce(newState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

export const onSetTextOverlaySingleRange: Re<{
  groupId: string;
  startTimeMs: number;
  stopTimeMs: number;
}> = (state, { payload }) => {
  const group = state.udrAsset.boundingPolys[payload.groupId];
  if (!group) {
    return;
  }

  const mediaDuration = state.mediaDuration;
  const fps = state.tdo?.details?.veritoneFile?.videoFrameRate;

  let startTimeMs = clamp(payload.startTimeMs, 0, mediaDuration * 1000 - 1);
  let stopTimeMs = clamp(payload.stopTimeMs, 0, mediaDuration * 1000);
  if (fps) {
    startTimeMs = frameAlignStartTimeMs(startTimeMs, fps);
    stopTimeMs = frameAlignStopTimeMs(stopTimeMs, fps);
    stopTimeMs = Math.ceil(stopTimeMs - 1);
  }
  if (startTimeMs >= stopTimeMs) {
    return;
  }

  // Keep the box's CURRENT geometry when collapsing to a single full-video
  // segment. Using basePoly reverted the box to its original creation size, so
  // "Full" cut a resized box back down and clipped its text (VE-25637 #7).
  // The anchor-segment preference that fixed it is no longer needed: a text
  // overlay's box is group-level, so there are no stale siblings to pick
  // between (VE-30942).
  const currentBoundingPoly = resolveTextOverlayRect(group);
  if (!currentBoundingPoly) {
    return;
  }
  const polyId = uuid();
  const newItem = {
    id: polyId,
    startTimeMs,
    stopTimeMs,
    object: {
      id: polyId,
      overlayObjectType: 'text_overlay' as const,
      boundingPoly: currentBoundingPoly,
    },
  };

  // Replace every existing segment with the single new one.
  const selectedPolyGroups = { ...state.selectedPolyGroups };
  group.series.forEach((s) => delete selectedPolyGroups[s.id]);
  selectedPolyGroups[polyId] = true;

  const [newState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      const draftGroup = draft.udrAsset.boundingPolys[payload.groupId];
      if (!draftGroup) {
        return;
      }
      draftGroup.series = [newItem];
      draftGroup.status = UDRsPolyAssetStatus.SINGLE;

      draft.tdoIsChanged = true;
      draft.highlightedOverlay = {
        id: polyId,
        timeMs: startTimeMs,
        groupId: payload.groupId,
        type: 'text_overlay',
      };
      draft.selectedPolyGroups = selectedPolyGroups;
    }
  );

  return produce(newState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

export const onDeleteOverlay: Re<{
  readonly boundingBox: { id: string; groupId: string };
}> = (state, { payload }) => {
  const groupId = payload.boundingBox.groupId;
  const deletedObjectId = payload.boundingBox.id;

  const boundingPolysGroup = groupId
    ? state.udrAsset.boundingPolys[groupId]
    : null;

  if (groupId && boundingPolysGroup?.series) {
    const [newState, patches, inversePatches] = produceWithPatches(
      state,
      (draft) => {
        draft.tdoIsChanged = true;
        draft.highlightedOverlay = undefined;
        delete draft.selectedPolyGroups[deletedObjectId];
        if (boundingPolysGroup.series.length < 2) {
          deleteUdrGroupHelper(draft, groupId, boundingPolysGroup);
        } else {
          const remainingSeries = boundingPolysGroup.series.filter(
            (s) => s.id !== deletedObjectId
          );
          draft.udrAsset.boundingPolys[groupId] = {
            ...boundingPolysGroup,
            series: remainingSeries,
            status:
              remainingSeries.length > 1
                ? UDRsPolyAssetStatus.GROUP
                : UDRsPolyAssetStatus.SINGLE,
          };
        }
      }
    );

    return produce(newState, (draft) => {
      draft.history.past.push({ patches, inversePatches });
      draft.history.future = [];
    });
  }
};

export const onDeleteOverlayInFrame: Re<{
  readonly boundingBox: { id: string; groupId: string };
  readonly timeMs: number;
}> = (state, { payload }) => {
  const groupId = payload.boundingBox.groupId;
  const deletedObjectId = payload.boundingBox.id;

  const boundingPolysGroup = groupId
    ? state.udrAsset.boundingPolys[groupId]
    : null;

  const curGroupSeries = boundingPolysGroup?.series;

  if (groupId && curGroupSeries) {
    const fps = state.tdo?.details?.veritoneFile?.videoFrameRate || 30;
    const frameStartTimeMs = frameAlignStartTimeMs(payload.timeMs, fps);
    const frameStopTimeMs = frameAlignStopTimeMs(
      frameStartTimeMs + 1000 / fps,
      fps
    );

    const selectGroupIds: { [id: string]: boolean } = {};

    const seriesStart = sliceUdrGroupSeriesEndAtTime(
      curGroupSeries,
      frameStartTimeMs
    );
    let seriesEnd = sliceUdrGroupSeriesStartAtTime(
      curGroupSeries,
      frameStopTimeMs
    );

    const lastItemFromSeriesStart = seriesStart.at(-1);
    const firstItemFromSeriesEnd = seriesEnd[0];

    // update left-end uuid
    if (
      lastItemFromSeriesStart &&
      lastItemFromSeriesStart.id === deletedObjectId
    ) {
      const newId = uuid();
      const newLastItemFromSeriesStart = {
        ...lastItemFromSeriesStart,
        id: newId,
        object: {
          ...lastItemFromSeriesStart.object,
          id: newId,
        },
      };

      seriesStart.pop(); // remove last item
      seriesStart.push(newLastItemFromSeriesStart);
      selectGroupIds[newId] = true;
    }

    // update right-end uuid
    if (
      firstItemFromSeriesEnd &&
      firstItemFromSeriesEnd.id === deletedObjectId
    ) {
      const newId = uuid();
      const newFirstItemFromSeriesEnd = {
        ...firstItemFromSeriesEnd,
        id: newId,
        object: {
          ...firstItemFromSeriesEnd.object,
          id: newId,
        },
      };

      seriesEnd.shift(); // remove first item
      seriesEnd = [newFirstItemFromSeriesEnd].concat(seriesEnd);
      selectGroupIds[newId] = true;
    }
    const series = seriesStart.concat(seriesEnd);

    const [newState, patches, inversePatches] = produceWithPatches(
      state,
      (draft) => {
        draft.tdoIsChanged = true;
        draft.highlightedOverlay = undefined;
        draft.selectedPolyGroups = {
          ...state.selectedPolyGroups,
          ...selectGroupIds,
        };
        delete draft.selectedPolyGroups[deletedObjectId];
        if (series.length === 0) {
          // completely delete group
          deleteUdrGroupHelper(draft, groupId, boundingPolysGroup);
        } else {
          draft.udrAsset.boundingPolys[groupId] = {
            ...boundingPolysGroup,
            series,
          };
        }
      }
    );

    return produce(newState, (draft) => {
      draft.history.past.push({ patches, inversePatches });
      draft.history.future = [];
    });
  }
};

export const onDeleteOverlaySegment: Re<actions.DeleteSegmentPayload> = (
  state,
  action
) => {
  const { timeMs, segmentId, groupId } = action.payload;

  const boundingPolysGroup = groupId
    ? state.udrAsset.boundingPolys[groupId]
    : null;

  if (groupId && boundingPolysGroup?.series) {
    // separate segments from series
    const segments = getUDRGroupIntervalBasedSegments(
      boundingPolysGroup.series,
      groupId
    );

    // determine time range to remove
    let segmentStartTimeMs = 0;
    let segmentStopTimeMs =
      boundingPolysGroup.series[boundingPolysGroup.series.length - 1]
        ?.stopTimeMs || 0;

    // find segment
    let deleteSegment;
    if (segmentId) {
      deleteSegment = segments.find((s) => s.id === segmentId);
    } else {
      deleteSegment = segments.find(
        (s) => s.startTimeMs <= timeMs && s.stopTimeMs > timeMs
      );
    }

    if (deleteSegment) {
      segmentStartTimeMs = deleteSegment.startTimeMs;
      segmentStopTimeMs = deleteSegment.stopTimeMs;
    }

    const [newState, patches, inversePatches] = produceWithPatches(
      state,
      (draft) => {
        draft.tdoIsChanged = true;
        draft.highlightedOverlay = undefined;

        if (segments.length === 1) {
          // only one segment remove group entirely
          deleteUdrGroupHelper(draft, groupId, boundingPolysGroup);
        } else {
          const newSeries: UDRsPolyAssetGroupSeriesItem[] = [];

          boundingPolysGroup.series.forEach((item) => {
            if (
              item.stopTimeMs <= segmentStartTimeMs ||
              item.startTimeMs >= segmentStopTimeMs
            ) {
              newSeries.push(item); // keep
            } else {
              delete draft.selectedPolyGroups[item.id]; // remove from selected
            }
          });

          // remove span of segment
          draft.udrAsset.boundingPolys[groupId] = {
            ...boundingPolysGroup,
            series: newSeries,
            status:
              newSeries.length > 1
                ? UDRsPolyAssetStatus.GROUP
                : UDRsPolyAssetStatus.SINGLE,
          };
        }
      }
    );

    return produce(newState, (draft) => {
      draft.history.past.push({ patches, inversePatches });
      draft.history.future = [];
    });
  }
  return state;
};

export const onDeleteOverlayGroup: Re<{
  readonly boundingBox: { id: string; groupId: string };
}> = (state, { payload }) => {
  const groupId = payload.boundingBox.groupId;

  const boundingPolysGroup = groupId
    ? state.udrAsset.boundingPolys[groupId]
    : null;

  if (groupId && boundingPolysGroup) {
    const [newState, patches, inversePatches] = produceWithPatches(
      state,
      (draft) => {
        deleteUdrGroupHelper(draft, groupId, boundingPolysGroup);
      }
    );

    return produce(newState, (draft) => {
      draft.history.past.push({ patches, inversePatches });
      draft.history.future = [];
    });
  }

  return state;
};

export const removeUdrGroupFromClusterMapItem = (
  draft: Draft<WorkerState>,
  clusterId: string | undefined,
  groupId: string
) => {
  // remove groupId from the clusterMap[boundingPolysGroup.clusterId].udrGroupIds
  if (clusterId) {
    const clusterMapItem = draft.clusterMap[clusterId];
    if (clusterMapItem) {
      const index = clusterMapItem.udrGroupIds.indexOf(groupId);
      if (index > -1) {
        clusterMapItem.udrGroupIds.splice(index, 1);
      }

      // if cluster has no remaining assigned groups it can be removed
      if (
        clusterMapItem.detectionGroupIds.length +
          clusterMapItem.udrGroupIds.length ===
        0
      ) {
        delete draft.clusterMap[clusterId];
      }
    }
  }
};

export const deleteUdrGroupHelper = (
  draft: Draft<WorkerState>,
  groupId: string,
  boundingPolysGroup: UDRsPolyAssetGroup | null | undefined
) => {
  if (!boundingPolysGroup) {
    return;
  }
  delete draft.selectedPolyGroups[groupId]; // we may eventually stop saving this
  if (boundingPolysGroup.basePoly) {
    // ideally this would have already been removed - when udr was converted from static to spray-paint.
    // Absent entirely on text overlays, which have no OT seed (VE-30942).
    delete draft.selectedPolyGroups[boundingPolysGroup.basePoly.id];
  }

  // delete all items from selectedPolyGroups
  boundingPolysGroup.series.forEach((item) => {
    delete draft.selectedPolyGroups[item.id];
  });

  draft.tdoIsChanged = true;
  draft.highlightedOverlay = undefined;
  draft.udrAsset.boundingPolys[groupId] = null; // delete would be cleaner - but doesn't update timeline

  const clusterId = boundingPolysGroup.clusterId;
  removeUdrGroupFromClusterMapItem(draft, clusterId, groupId);
};

const onPatchLocalUdrAssets: Re<UDRsPolyAsset> = (state, { payload }) => {
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      for (const [id, poly] of Object.entries(payload.boundingPolys)) {
        if (!isEqual(poly, draft.udrAsset.boundingPolys[id])) {
          Object.assign(draft.udrAsset.boundingPolys, { [id]: poly });
        }
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

const onSetSelectedUDRGroupId: Re<{ readonly groupId: string | undefined }> = (
  state,
  { payload }
) => ({
  ...state,
  udrAsset: {
    ...state.udrAsset,
    selectedUDRGroupId: payload.groupId,
  },
});

const onUpdateUDRGroupLabel: Re<{ groupId: string; label: string }> = (
  state,
  { payload }
) =>
  produce(state, (draft) => {
    const udrGroup = draft.udrAsset.boundingPolys[payload.groupId];
    if (!udrGroup) {
      return;
    }

    Object.assign(udrGroup, { userLabel: payload.label });

    if (udrGroup.clusterId) {
      // update cluster label as well if cluster has single group
      const clusterMapItem = draft.clusterMap[udrGroup.clusterId];
      if (
        clusterMapItem?.udrGroupIds.length === 1 &&
        clusterMapItem?.detectionGroupIds.length === 0
      ) {
        clusterMapItem.userLabel = payload.label;
      }
    }
  });

export const onChangeOverlay: Re<{
  id: string;
  groupId: string;
  boundingPoly: BoundingPolyRect;
  type: GROUPED_POLY_TYPE;
}> = (state, { payload }) => {
  if (!isUdrAssetPolyType(payload.type)) {
    return;
  }

  const group = state.udrAsset.boundingPolys[payload.groupId];
  if (!group) {
    return;
  }

  // See onChangeOverlayWithPatches below — kept in sync with that copy
  // deliberately; both are geometry writers.
  const nextGroup = applyGeometryWrite(group, payload.id, payload.boundingPoly);

  return produce(state, (draft) => {
    draft.tdoIsChanged = true;
    Object.assign(draft.udrAsset.boundingPolys, {
      [group.groupId]: nextGroup,
    });
  });
};

/**
 * Apply a geometry write to a group.
 *
 * A text overlay's incoming box is always the DERIVED one — the caller hugs it
 * to the text via computeTextOverlayPoly before dispatching — and it lands in
 * two different places depending on what owns the position:
 *
 *  - The series rects always take it. They are the cache the burn-in engine
 *    reads: the engine positions text from the rect, not from the pins (a stale
 *    rect is what burned in truncated in VE-30798, while the pins in that same
 *    payload were correct). Every segment takes the same value — a text
 *    overlay's box is group-level.
 *  - `textOverlay.customRect` takes it ONLY when no screen-position pin is set,
 *    because that is the authored position. For a PINNED overlay the position
 *    is owned by the pins and a customRect would be derived state sitting in an
 *    authored field — dead while pinned, and liable to resurface as the box if
 *    the pins were ever cleared. The load adapter skips the hoist for pinned
 *    overlays for the same reason; this keeps the two paths agreeing.
 *
 * A UDR keeps its per-segment write: its geometry genuinely varies over time
 * via spray paint and optical tracking, which is the point of its series.
 */
function applyGeometryWrite(
  group: UDRsPolyAssetGroup,
  segmentId: string,
  boundingPoly: BoundingPolyRect
): UDRsPolyAssetGroup {
  const version = (group.version ?? 0) + 1;

  if (isTextOnlyOverlayGroup(group) && group.textOverlay) {
    const isPinned =
      !!group.textOverlay.screenPositionHorizontal ||
      !!group.textOverlay.screenPositionVertical;

    return {
      ...group,
      version,
      textOverlay: isPinned
        ? group.textOverlay
        : { ...group.textOverlay, customRect: boundingPoly },
      series: group.series.map((s) => ({
        ...s,
        object: { ...s.object, boundingPoly },
      })),
    };
  }

  return {
    ...group,
    version,
    series: group.series.map((s) =>
      s.id === segmentId ? { ...s, object: { ...s.object, boundingPoly } } : s
    ),
  };
}

export const onChangeOverlayWithPatches = (
  state: Draft<WorkerState>,
  {
    payload,
  }: {
    payload: {
      id: string;
      groupId: string;
      boundingPoly: BoundingPolyRect;
      type: GROUPED_POLY_TYPE;
      timeMs: number;
    };
  }
) => {
  if (!isUdrAssetPolyType(payload.type)) {
    return;
  }

  const group = state.udrAsset.boundingPolys[payload.groupId];
  if (group) {
    // A text overlay's box is authored state on the overlay, not per-segment
    // geometry — see applyGeometryWrite above.
    const nextGroup = applyGeometryWrite(
      group,
      payload.id,
      payload.boundingPoly
    );

    return produceWithPatches(state, (draft) => {
      draft.tdoIsChanged = true;
      draft.highlightedOverlay = {
        id: payload.id,
        groupId: payload.groupId,
        type: payload.type,
        timeMs: payload.timeMs,
      };

      Object.assign(draft.udrAsset.boundingPolys, {
        [group.groupId]: nextGroup,
      });
    });
  }

  return produceWithPatches(state, (draft) => {
    draft.highlightedOverlay = {
      id: payload.id,
      groupId: payload.groupId,
      type: payload.type,
      timeMs: payload.timeMs,
    };
  });
};

function nextTextOverlayLabel(
  boundingPolys: Readonly<Record<string, UDRsPolyAssetGroup | null>>
) {
  const re = /^Text (\d+)$/;
  let lastGroupNum = 0;
  for (const boundingPoly of Object.values(boundingPolys)) {
    if (boundingPoly && !isNil(boundingPoly.userLabel)) {
      const m = boundingPoly.userLabel.match(re);
      if (m) {
        lastGroupNum = Math.max(parseInt(m[1]!, 10), lastGroupNum);
      }
    }
  }

  return `Text ${lastGroupNum + 1}`;
}

function nextGroupLabel(
  boundingPolys: Readonly<Record<string, UDRsPolyAssetGroup | null>>
) {
  const re = /^Overlay Group (\d+)$/;
  let lastGroupNum = 0;
  for (const boundingPoly of Object.values(boundingPolys)) {
    if (boundingPoly && !isNil(boundingPoly.userLabel)) {
      const m = boundingPoly.userLabel.match(re);
      if (m) {
        lastGroupNum = Math.max(parseInt(m[1]!, 10), lastGroupNum); // Safe due to regex
      }
    }
  }

  return `Overlay Group ${lastGroupNum + 1}`;
}

function isTimeDecreasingSeries(
  series: { time: number; boundingPoly: BoundingPolyRect }[]
) {
  if (series.length === 1) {
    return false;
  }

  for (let i = 0; i < series.length - 1; i++) {
    // Safe due to for loop condition
    if (series[i]!.time < series[i + 1]!.time) {
      return false;
    }
  }

  return true;
}

function removeRepeatingContiguousDataPoints(
  series: { time: number; boundingPoly: BoundingPolyRect }[]
) {
  const shouldKeepItemAtIndex: boolean[] = [];

  for (let i = 0; i < series.length; i++) {
    const curSeriesItem = series[i]!; // Safe due to for loop condition
    const nextSeriesItem = series[i + 1];

    if (nextSeriesItem && curSeriesItem.time === nextSeriesItem.time) {
      shouldKeepItemAtIndex[i] = false;
      continue;
    }

    shouldKeepItemAtIndex[i] = true;
    if (nextSeriesItem) {
      shouldKeepItemAtIndex[i + 1] = true;
    }
  }

  return series.filter((_, idx) => shouldKeepItemAtIndex[idx]);
}

function getBoundingPolyIntervalsFromNonRepeatingSeries(
  series: { time: number; boundingPoly: BoundingPolyRect }[],
  fps: number | undefined
) {
  const shouldRemoveItemAtIndex: boolean[] = [];

  for (let i = 0; i < series.length; i++) {
    const curSeriesItem = series[i]!; // Safe due to for loop condition
    const nextSeriesItem = series[i + 1];

    if (nextSeriesItem && nextSeriesItem.time < curSeriesItem.time) {
      let j = i;

      let otherItem: ElementOf<typeof series> | undefined;
      while ((otherItem = series[j]) && nextSeriesItem.time <= otherItem.time) {
        shouldRemoveItemAtIndex[j] = true;
        j--;
      }
    }
  }

  const cleanSeries = series.filter((_, i) => !shouldRemoveItemAtIndex[i]);

  const intervals = [];

  let startTimeMs = cleanSeries?.[0]?.time ?? 0; // start of first interval
  // if fps is available align intervals with frame boundaries
  if (fps && cleanSeries[0]) {
    startTimeMs = frameAlignStartTimeMs(startTimeMs, fps);
  }

  for (let i = 0; i < cleanSeries.length; i++) {
    const curSeriesItem = cleanSeries[i]!; // Safe due to for loop condition
    const nextSeriesItem = cleanSeries[i + 1];

    // if next boundingPoly is identical keep the current interval open
    if (nextSeriesItem) {
      if (
        checkSameBoundingPoly(
          curSeriesItem.boundingPoly,
          nextSeriesItem.boundingPoly
        )
      ) {
        continue;
      }
    }

    // default stopTimeMs - extends to the time of the next item frame (or current frame + 50ms if final item)
    let stopTimeMs = nextSeriesItem
      ? nextSeriesItem.time
      : curSeriesItem.time + 50;

    let interval;

    // if fps is available align intervals with frame boundaries
    if (fps) {
      const curItemFrameIndex = findFrameIndex(curSeriesItem.time, fps);

      // interval extends to the start of the next item frame (or end of current frame if final item)
      const nextItemFrameIndex = nextSeriesItem
        ? findFrameIndex(nextSeriesItem.time, fps)
        : curItemFrameIndex + 1;

      // if next item is the in same frame as current item keep the interval open
      if (nextItemFrameIndex === curItemFrameIndex) {
        continue;
      }

      // convert frame index back to timeMs
      stopTimeMs = convertFrameIndexToTimeMs(nextItemFrameIndex, fps);

      interval = {
        startTimeMs: Math.ceil(startTimeMs), // sub ms precision not needed anymore!
        stopTimeMs: Math.ceil(stopTimeMs - 1), // back off so displays as 1ms less the start time for next frame
        boundingPoly: curSeriesItem.boundingPoly,
      };
    } else {
      interval = {
        startTimeMs: Number(startTimeMs.toFixed(1)),
        stopTimeMs: Number(stopTimeMs.toFixed(1)),
        boundingPoly: curSeriesItem.boundingPoly,
      };
    }

    intervals.push(interval);

    startTimeMs = stopTimeMs; // update startTimeMs for next interval
  }

  return intervals;
}

export function removeSliceFromUdrGroupSeries(
  series: ReadonlyArray<UDRsPolyAssetGroupSeriesItem>,
  sliceStartTimeMs: number,
  sliceStopTimeMs: number
): UDRsPolyAssetGroupSeriesItem[] {
  const seriesStart = sliceUdrGroupSeriesEndAtTime(series, sliceStartTimeMs);

  const seriesEnd = sliceUdrGroupSeriesStartAtTime(series, sliceStopTimeMs);

  return seriesStart.concat(seriesEnd);
}

export function sliceUdrGroupSeriesEndAtTime(
  series: ReadonlyArray<UDRsPolyAssetGroupSeriesItem>,
  time: number
): UDRsPolyAssetGroupSeriesItem[] {
  const result = [];

  for (const curItem of series) {
    if (
      Math.ceil(curItem.startTimeMs) < time &&
      Math.floor(curItem.stopTimeMs) > time
    ) {
      result.push({
        ...curItem,
        stopTimeMs: time,
      });
    } else if (Math.floor(curItem.stopTimeMs) <= time) {
      result.push(curItem);
    } else {
      break;
    }
  }

  return result;
}

export function sliceUdrGroupSeriesStartAtTime(
  series: ReadonlyArray<UDRsPolyAssetGroupSeriesItem>,
  time: number
): UDRsPolyAssetGroupSeriesItem[] {
  const result = [];

  for (const curItem of series) {
    if (
      Math.ceil(curItem.startTimeMs) < time &&
      Math.floor(curItem.stopTimeMs) > time
    ) {
      result.push({
        ...curItem,
        startTimeMs: time,
      });
    } else if (Math.ceil(curItem.startTimeMs) >= time) {
      result.push(curItem);
    }
  }

  return result;
}

export function insertUdrGroupSeries(
  curSelectedIds: { [id: string]: boolean | undefined },
  curGroupSeries: ReadonlyArray<UDRsPolyAssetGroupSeriesItem>,
  insertSeries: ReadonlyArray<UDRsPolyAssetGroupSeriesItem>
): [UDRsPolyAssetGroupSeriesItem[], { [id: string]: boolean }] {
  // inserts insertSeries into the existing curGroupSeries
  // this currently only supports insertSeries that are continuous
  // all new items will be set to selected

  const selectGroupIds: { [id: string]: boolean } = {};

  const firstInsertSeriesItem = insertSeries[0]!; // Safe due to series item having at least 1 item - (intervals map)
  const lastInsertSeriesItem = insertSeries.at(-1)!; // Safe due to seriesItem having at least 1 item

  const seriesStart = sliceUdrGroupSeriesEndAtTime(
    curGroupSeries,
    firstInsertSeriesItem.startTimeMs
  );
  const seriesEnd = sliceUdrGroupSeriesStartAtTime(
    curGroupSeries,
    lastInsertSeriesItem.stopTimeMs
  );

  const lastItemFromSeriesStart = seriesStart.at(-1);
  const firstItemFromSeriesEnd = seriesEnd[0];

  // if insertion splits an existing udr object new to create a new object at the right end and inherit selected status
  if (
    lastItemFromSeriesStart &&
    firstItemFromSeriesEnd &&
    lastItemFromSeriesStart.id === firstItemFromSeriesEnd.id &&
    seriesEnd[0]
  ) {
    const newId = uuid();
    Object.assign(seriesEnd[0], {
      id: newId,
      object: { ...seriesEnd[0].object, id: newId },
    });
    if (curSelectedIds) {
      selectGroupIds[newId] =
        curSelectedIds[lastItemFromSeriesStart.id] || false;
    }
  }

  // splice in the new series
  const series = seriesStart.concat(insertSeries, seriesEnd);

  // set selected for all new series items
  for (const seriesItem of insertSeries) {
    selectGroupIds[seriesItem.id] = true;
  }

  return [series, selectGroupIds];
}

const clampedBoundingPolyRect = (orig: BoundingPolyRect): BoundingPolyRect => [
  {
    x: clamp(orig[0].x, 0, 1),
    y: clamp(orig[0].y, 0, 1),
  },
  {
    x: clamp(orig[1].x, 0, 1),
    y: clamp(orig[1].y, 0, 1),
  },
  {
    x: clamp(orig[2].x, 0, 1),
    y: clamp(orig[2].y, 0, 1),
  },
  {
    x: clamp(orig[3].x, 0, 1),
    y: clamp(orig[3].y, 0, 1),
  },
];

export const onSprayPaintSave: Re<{
  id: string;
  groupId: string;
  series: { boundingPoly: BoundingPolyRect; time: number }[];
}> = (state, { payload }) => {
  const nonRepeatingSeries = removeRepeatingContiguousDataPoints(
    payload.series
  ).map((series) => ({
    ...series,
    boundingPoly: clampedBoundingPolyRect(series.boundingPoly),
  }));

  if (isTimeDecreasingSeries(nonRepeatingSeries)) {
    return;
  }

  //* If nonRepeating series is a single point can update the existing overlay */
  if (arrayHasLength(nonRepeatingSeries, 1)) {
    const newState = onChangeOverlayWithPatches(state, {
      payload: {
        id: payload.id,
        groupId: payload.groupId,
        boundingPoly: nonRepeatingSeries[0].boundingPoly,
        type: 'udr',
        timeMs: payload.series.at(-1)!.time,
      },
    });

    const [nextState, patches, inversePatches] = produceWithPatches(
      newState?.[0] ?? state,
      (draft) => {
        draft.udrAsset.lastSprayPaintSeriesItemId = payload.id;
        draft.newSprayPaintEditId = undefined;

        // check if we should create background blur
        const globalSettings = selectGlobalSettings({
          [namespace]: draft,
        });

        let addBackgroundBlur = false;
        let backgroundBlurLevel = globalSettings.defaultBackgroundBlurLevel;
        if (
          globalSettings.autoAddBackgroundBlur &&
          globalSettings.objectTypeEffects['udr'].redactionConfig.fillType ===
            'inverse_blur' &&
          !draft.udrAsset.boundingPolys[payload.groupId]?.redactionConfig
        ) {
          addBackgroundBlur = true;
        }
        if (
          draft.udrAsset.boundingPolys[payload.groupId]?.redactionConfig
            ?.addBackgroundBlur
        ) {
          addBackgroundBlur = true;
          backgroundBlurLevel =
            draft.udrAsset.boundingPolys[payload.groupId]?.redactionConfig
              ?.addBackgroundBlur;
        }

        if (addBackgroundBlur && backgroundBlurLevel) {
          addBackgroundBlurEdits(
            draft,
            [payload.groupId],
            [],
            backgroundBlurLevel
          );
        }
      }
    );

    return produce(nextState, (draft) => {
      draft.history.past.push({
        patches: [...(newState?.[1] ?? []), ...patches],
        inversePatches: [...(newState?.[2] ?? []), ...inversePatches],
      });
      draft.history.future = [];
    });
  }

  /* Construct intervals */
  const fps = state.tdo?.details?.veritoneFile?.videoFrameRate;
  const intervals = getBoundingPolyIntervalsFromNonRepeatingSeries(
    nonRepeatingSeries,
    fps
  );

  /* Check if no intervals! - in theory this should never happen */
  if (!arrayHasContents(intervals)) {
    return;
  }

  const sprayPaintEditId = uuid();

  const seriesItems = intervals.map(
    (interval: {
      boundingPoly: BoundingPolyRect;
      startTimeMs: number;
      stopTimeMs: number;
    }) => {
      const id = uuid();
      return {
        id,
        startTimeMs: interval.startTimeMs,
        stopTimeMs: interval.stopTimeMs,
        object: {
          id,
          overlayObjectType: 'spray_paint_udr' as const,
          boundingPoly: interval.boundingPoly,
        },
        sprayPaintEditId,
      };
    }
  );

  const groupId = payload.groupId;

  const curGroup = state.udrAsset.boundingPolys[groupId];
  if (curGroup) {
    const curGroupSeries = curGroup?.series || [];

    const [series, selectGroupIds] = insertUdrGroupSeries(
      state.selectedPolyGroups,
      curGroupSeries,
      seriesItems
    );

    const udrPolyAssetGroup = {
      ...curGroup,
      series,
    };

    const lastSprayPaintSeriesItemId = seriesItems.at(-1)?.id;

    const [nextState, patches, inversePatches] = produceWithPatches(
      state,
      (draft) => {
        draft.tdoIsChanged = true;
        Object.assign(draft.selectedPolyGroups, {
          // [groupId]: true,  // GTODO probably not needed
          ...selectGroupIds,
        });
        draft.newSprayPaintEditId = sprayPaintEditId;
        draft.udrAsset.boundingPolys[groupId] = udrPolyAssetGroup;
        draft.udrAsset.lastSprayPaintSeriesItemId = lastSprayPaintSeriesItemId;

        // check if we should create background blur
        const globalSettings = selectGlobalSettings({
          [namespace]: draft,
        });

        let addBackgroundBlur = false;
        let backgroundBlurLevel = globalSettings.defaultBackgroundBlurLevel;
        if (
          globalSettings.autoAddBackgroundBlur &&
          globalSettings.objectTypeEffects['udr'].redactionConfig.fillType ===
            'inverse_blur' &&
          !draft.udrAsset.boundingPolys[payload.groupId]?.redactionConfig
        ) {
          addBackgroundBlur = true;
        }
        if (
          draft.udrAsset.boundingPolys[payload.groupId]?.redactionConfig
            ?.addBackgroundBlur
        ) {
          addBackgroundBlur = true;
          backgroundBlurLevel =
            draft.udrAsset.boundingPolys[payload.groupId]?.redactionConfig
              ?.addBackgroundBlur;
        }

        if (addBackgroundBlur && backgroundBlurLevel) {
          addBackgroundBlurEdits(
            draft,
            [payload.groupId],
            [],
            backgroundBlurLevel
          );
        }
      }
    );

    return produce(nextState, (draft) => {
      draft.history.past.push({ patches, inversePatches });
      draft.history.future = [];
    });
  }
};

export default createReducer(defaultState, (builder) => {
  builder
    .addCase(actions.NEW_OVERLAY, onNewOverlay)
    .addCase(actions.ADD_TEXT_OVERLAY_SEGMENT, onAddTextOverlaySegment)
    .addCase(actions.MERGE_TEXT_OVERLAY_SEGMENTS, onMergeTextOverlaySegments)
    .addCase(actions.SET_TEXT_OVERLAY_SINGLE_RANGE, onSetTextOverlaySingleRange)
    .addCase(actions.DELETE_UDR_IN_FRAME, onDeleteOverlayInFrame)
    .addCase(actions.DELETE_UDR_OVERLAY, onDeleteOverlay)
    .addCase(actions.DELETE_UDR_SEGMENT, onDeleteOverlaySegment)
    .addCase(actions.DELETE_UDR_GROUP, onDeleteOverlayGroup)
    .addCase(actions.SET_SELECTED_UDR_GROUP, onSetSelectedUDRGroupId)
    .addCase(
      actions.SET_SELECTED_UDR_GROUP_ON_WEB_WORKER_ONLY,
      onSetSelectedUDRGroupId
    )
    .addCase(actions.CHANGE_OVERLAY, onChangeOverlay)
    .addCase(actions.CHANGE_UDR_GROUP_LABEL, onUpdateUDRGroupLabel)
    .addCase(actions.ON_SPRAY_PAINT_SAVE, onSprayPaintSave)
    .addCase(actions.SAVE_LOCAL_UDR_ASSETS, onPatchLocalUdrAssets);
});

const clampPoint = ({ x, y }: Point) => ({
  x: clamp(x, 0, 1),
  y: clamp(y, 0, 1),
});
